import React, { useState, useEffect } from "react";
import { Tab, Tabs } from "react-bootstrap";
import Giphy_logo from "../../../../../../../../assets/images/Giphy logo.png";
import GifSection from "./gifSections/GifSection";
import Button from "../../../../../../Elements/Button";
import UnsplashSection from "./gifSections/UnsplashSection";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileCirclePlus, faArrowsRotate } from "@fortawesome/free-solid-svg-icons";
import CookieBlockedMessage from "../../../../../../CommonComponents/CookieBlockedMessage";
import { useAcceptAll } from "../../../../../../../hooks/useCookieChoiceMade";

const ImageSection = ({
  pickInPreview = false,
  slideCount = null,
  tool,
  handleSelectGifs,
  handleImageUpload,
  uploadElement,
  editElement,
  errorState,
}) => {
  const getContentType = (media) => media?.substring(6)?.toUpperCase();
  const getContentSize = (media) => (media / 1024)?.toFixed(2);
  const [imageDimensions, setImageDimensions] = useState(null);
  const [gifReloadKey, setGifReloadKey] = useState(0);
  const [objectUrl, setObjectUrl] = useState(null);
  const acceptAll = useAcceptAll();
  const gifPickerBlocked = <CookieBlockedMessage contentType="content" large style={{ width: "100%", minHeight: "220px" }} />;

  useEffect(() => {
    if (!tool?.file) {
      setObjectUrl(null);
      return;
    }
    const url = URL.createObjectURL(tool.file);
    setObjectUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [tool?.file]);

  // Elements placed from the retired Tenor picker open in the Unsplash tab, which still previews them.
  const tabFromGifType = (gifType) =>
    gifType === "sticker" ? "gifs" : ["unsplash", "tenor"].includes(gifType) ? "unsplash" : "image";

  const [activeTab, setActiveTab] = useState(() => tabFromGifType(editElement.gif_type));

  useEffect(() => {
    if (!tool?.file) {
      setActiveTab(tabFromGifType(editElement.gif_type));
    }
  }, [editElement?.id, editElement?.gif_type]);

  useEffect(() => {
    if (pickInPreview && tool?.value?.gif_link) setActiveTab("image");
  }, [tool?.value?.gif_link]);

  if (pickInPreview) {
    const localSrc = objectUrl ?? (tool?.value?.gif_link || editElement?.file_url);
    const hasLocal = !!(tool?.file || editElement?.file_url);
    const dims = imageDimensions ? `${imageDimensions.width} × ${imageDimensions.height}` : null;
    const urlName = editElement?.file_url && !editElement.file_url.startsWith("data:")
      ? editElement.file_url.split("/").pop().split("?")[0]
      : null;
    const fileName = tool?.file?.name ?? editElement?.file_details?.name ?? urlName;
    const sizeBytes = tool?.file?.size ?? editElement?.file_details?.file_size;
    const meta = tool?.value?.gif_link && !tool?.file
      ? ["Animated GIF", dims].filter(Boolean).join(" · ")
      : hasLocal
        ? [fileName, dims, sizeBytes ? `${getContentSize(sizeBytes)} KB` : null].filter(Boolean).join(" · ")
        : "No image selected. Click the preview below or choose a file.";
    const gifOpen = activeTab !== "image";
    const slideMode = slideCount !== null;
    const rowMeta = slideMode
      ? (slideCount ? `${slideCount} slide${slideCount > 1 ? "s" : ""} in this slideshow` : "No slides yet. Click the preview below or add an image.")
      : meta;

    return (
      <>
        <div className="img-src-row">
          <span className="img-src-meta" title={rowMeta}>{rowMeta}</span>
          <label className="img-src-btn">
            <input
              type="file"
              accept="image/*"
              title=""
              key={tool?.file ? "file-uploaded" : "file-upload"}
              onChange={(e) => handleImageUpload(e, "upload")}
            />
            <FontAwesomeIcon icon={slideMode || !hasLocal ? faFileCirclePlus : faArrowsRotate} className="me-1" />
            {slideMode ? "Add image" : hasLocal ? "Replace" : "Choose file"}
          </label>
          {!slideMode && ((tool?.file && !editElement?.file_url) || tool?.value?.gif_link) && (
            <button
              type="button"
              className="img-src-btn img-src-btn--remove"
              onClick={(e) => { handleImageUpload(e, "cancel"); setGifReloadKey((k) => k + 1); }}
            >
              <i className="fa-regular fa-trash-can me-1" />
              Remove
            </button>
          )}
          <button
            type="button"
            className={`img-src-btn${gifOpen ? " active" : ""}`}
            aria-expanded={gifOpen}
            onClick={() => setActiveTab(gifOpen ? "image" : (["unsplash", "tenor"].includes(tool?.gif_type) ? "unsplash" : "gifs"))}
          >
            GIF
          </button>
        </div>
        {localSrc && (
          <img
            src={localSrc}
            alt=""
            className="d-none"
            onLoad={(e) => setImageDimensions({ width: e.target.naturalWidth, height: e.target.naturalHeight })}
          />
        )}
        {gifOpen && (
          <Tabs
            id="image-gif-picker"
            activeKey={activeTab}
            onSelect={(key) => {
              if (typeof key === "string") {
                setActiveTab(key);
              }
            }}
            className="select-file-pop img-gif-tabs"
          >
            <Tab eventKey="gifs" title={<img src={Giphy_logo} alt="Giphy logo" height="13px" width="63px" />}>
              {acceptAll ? <GifSection key={gifReloadKey} tool={tool} handleSelectGifs={handleSelectGifs} handleImageUpload={handleImageUpload} activeTab={activeTab} /> : gifPickerBlocked}
            </Tab>
            <Tab eventKey="unsplash" title="Unsplash">
              {acceptAll ? <UnsplashSection tool={tool} handleSelectGifs={handleSelectGifs} handleImageUpload={handleImageUpload} activeTab={activeTab} /> : gifPickerBlocked}
            </Tab>
          </Tabs>
        )}
        <p className="text-danger">{errorState?.imgErr}</p>
      </>
    );
  }

  return (
    <>
      <Tabs
        defaultActiveKey={tabFromGifType(editElement.gif_type)}
        id="uncontrolled-tab-example"
        activeKey={activeTab}
        onSelect={(key) => {
          if (typeof key === "string") {
            setActiveTab(key);
          }
        }}
        className="select-file-pop"
      >
        <Tab eventKey="image" title={"Local Files"}>
          <div className="local-files-main">
            {!tool?.file && !editElement?.file_url ? (
              <div className="local-files-empty">
                <div className="upload-file-btn select-img-pop">
                  <div className="input-select-wrap position-relative">
                    <input
                      type="file"
                      id="file-input"
                      accept="image/*"
                      title=""
                      key={tool?.file ? "file-uploaded" : "file-upload"}
                      onChange={(e) => handleImageUpload(e, "upload")}
                    />
                    <FontAwesomeIcon icon={faFileCirclePlus} className="me-2" />
                    Select File
                  </div>
                </div>
              </div>
            ) : (
              <div className="file-data-main">
                <div className="img-preview-wrap">
                  <img
                    src={objectUrl ?? editElement?.file_url}
                    style={{ opacity: `${tool?.transparency ?? 10}` }}
                    className="img-preview-thumb"
                    onLoad={(e) => {
                      const img = e.target;
                      setImageDimensions({ width: img.naturalWidth, height: img.naturalHeight });
                    }}
                  />
                </div>
                <div className="file-info">
                  <span>Kind: <strong>{getContentType(tool?.file?.type ?? editElement?.file_details?.content_type)} image</strong></span>
                  <span>Size: <strong>{getContentSize(tool?.file?.size ?? editElement?.file_details?.file_size)} KB</strong></span>
                  {imageDimensions && (
                    <span>Dimensions: <strong>{imageDimensions.width} × {imageDimensions.height}px</strong></span>
                  )}
                </div>
                <div className="img-action-row">
                  {editElement?.file_url && (
                    <div className="upload-file-btn select-img-pop">
                      <div className="input-select-wrap img-action-btn img-action-btn--replace position-relative">
                        <input
                          type="file"
                          id="file-input"
                          accept="image/*"
                          title=""
                          onChange={(e) => handleImageUpload(e, "upload")}
                        />
                        <FontAwesomeIcon icon={faArrowsRotate} className="me-2" />
                        Replace Image
                      </div>
                    </div>
                  )}
                  {!editElement?.file_url && (
                    <button
                      className="img-action-btn img-action-btn--remove"
                      onClick={(e) => { handleImageUpload(e, 'cancel'); setGifReloadKey(k => k + 1); }}
                    >
                      <i className="fa-regular fa-trash-can me-1" />
                      Remove Image
                    </button>
                  )}
                </div>
              </div>
            )}
            <p id="file-name-display" className="file-name-display"></p>
          </div>
        </Tab>
        <Tab
          eventKey="gifs"
          activeKey={activeTab}
          onSelect={(key) => {
            if (typeof key === "string") {
              setActiveTab(key);
            }
          }}
          title={
            <img src={Giphy_logo} alt="Giphy logo" height="13px" width="63px" />
          }
        >
          {acceptAll ? <GifSection key={gifReloadKey} tool={tool} handleSelectGifs={handleSelectGifs} handleImageUpload={handleImageUpload} activeTab={activeTab} /> : gifPickerBlocked}
        </Tab>
        <Tab
          eventKey="unsplash"
          activeKey={activeTab}
          onSelect={(key) => {
            if (typeof key === "string") {
              setActiveTab(key);
            }
          }}
          title={
            "Unsplash"
          }
        >
          {acceptAll ? <UnsplashSection tool={tool} handleSelectGifs={handleSelectGifs} handleImageUpload={handleImageUpload} activeTab={activeTab} /> : gifPickerBlocked}
        </Tab>
      </Tabs>
      <p className="text-danger">{errorState?.imgErr}</p>
    </>
  );
};
export default ImageSection;
